import React, { useState } from 'react';
import {
  MenuItemLink,
  MenuProps,
  useSidebarState,
} from 'react-admin';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
} from '@mui/material';
import {
  // Catalog icons
  Inventory as ProductsIcon,
  Storefront as BrandsIcon,
  Category as CategoriesIcon,
  LocalOffer as TagsIcon,
  Tune as AttributesIcon,
  Settings as AttributeGroupsIcon,
  Psychology as CharacteristicsIcon,
  AccountTree as CharacteristicGroupsIcon,
  Collections as CollectionsIcon,
  
  // Content icons
  Article as CasesIcon,
  Gavel as PrivacyIcon,
  Info as AboutIcon,
  AccountBalance as FinancingIcon,
  Contacts as ContactGroupsIcon,
  Work as VacanciesIcon,
  WorkOutline as VacancyCategoriesIcon,
  Description as VacancyPageDataIcon,
  
  // CRM icons
  ContactMail as InquiriesIcon,
  
  // System icons
  People as UsersIcon,
  Photo as MediaIcon,
  
  // Group icons
  ShoppingCart as CatalogIcon,
  ContentPaste as ContentIcon,
  SupportAgent as CRMIcon,
  AdminPanelSettings as SystemIcon,
  
  // Navigation icons
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';

interface MenuGroup {
  id: string;
  label: string;
  icon: React.ComponentType;
  color: string;
  resources: Array<{
    name: string;
    label: string;
    icon: React.ComponentType;
  }>;
}

const menuGroups: MenuGroup[] = [
  {
    id: 'catalog',
    label: 'Каталог',
    icon: CatalogIcon,
    color: '#1976d2',
    resources: [
      { name: 'products', label: 'Товары', icon: ProductsIcon },
      { name: 'brands', label: 'Бренды', icon: BrandsIcon },
      { name: 'categories', label: 'Категории', icon: CategoriesIcon },
      { name: 'collections', label: 'Коллекции', icon: CollectionsIcon },
      { name: 'tags', label: 'Теги', icon: TagsIcon },
      { name: 'attribute-groups', label: 'Группы атрибутов', icon: AttributeGroupsIcon },
      { name: 'attributes', label: 'Атрибуты', icon: AttributesIcon },
      { name: 'characteristic-groups', label: 'Группы характеристик', icon: CharacteristicGroupsIcon },
      { name: 'characteristics', label: 'Характеристики', icon: CharacteristicsIcon },
    ],
  },
  {
    id: 'vacancies',
    label: 'Вакансии',
    icon: VacanciesIcon,
    color: '#f57c00',
    resources: [
      { name: 'vacancy-categories', label: 'Категории вакансий', icon: VacancyCategoriesIcon },
      { name: 'vacancies', label: 'Вакансии', icon: VacanciesIcon },
      { name: 'vacancy-page-data', label: 'Контент страницы', icon: VacancyPageDataIcon },
    ],
  },
  {
    id: 'content',
    label: 'Контент',
    icon: ContentIcon,
    color: '#388e3c',
    resources: [
      { name: 'about', label: 'О нас', icon: AboutIcon },
      { name: 'financing', label: 'Финансирование', icon: FinancingIcon },
      { name: 'contact-groups', label: 'Группы контактов', icon: ContactGroupsIcon },
      { name: 'case-categories', label: 'Категории кейсов', icon: CategoriesIcon },
      { name: 'cases', label: 'Кейсы', icon: CasesIcon },
      { name: 'privacy-sections', label: 'Политика конфиденциальности', icon: PrivacyIcon },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: CRMIcon,
    color: '#7b1fa2',
    resources: [
      { name: 'inquiries', label: 'Заявки', icon: InquiriesIcon },
    ],
  },
  {
    id: 'system',
    label: 'Система',
    icon: SystemIcon,
    color: '#455a64',
    resources: [
      { name: 'users', label: 'Пользователи', icon: UsersIcon },
      { name: 'media', label: 'Медиа', icon: MediaIcon },
    ],
  },
];

export const CustomMenu: React.FC<MenuProps> = ({ dense = false }) => {
  const [open] = useSidebarState();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    catalog: true, // Каталог открыт по умолчанию
    vacancies: true, // Вакансии открыт по умолчанию
    content: true, // Контент открыт по умолчанию
  });

  const handleGroupToggle = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  return (
    <Box sx={{ width: open ? 240 : 50, transition: 'width 0.3s' }}>
      <List component="nav" dense={dense}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            {/* Group Header */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleGroupToggle(group.id)}
                sx={{
                  minHeight: 48,
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: group.color,
                  }}
                >
                  <group.icon />
                </ListItemIcon>
                {open && (
                  <>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: group.color,
                          }}
                        >
                          {group.label}
                        </Typography>
                      }
                    />
                    {expandedGroups[group.id] ? <ExpandLess /> : <ExpandMore />}
                  </>
                )}
              </ListItemButton>
            </ListItem>

            {/* Group Resources */}
            <Collapse in={expandedGroups[group.id] && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.resources.map((resource) => (
                  <MenuItemLink
                    key={resource.name}
                    to={`/${resource.name}`}
                    primaryText={resource.label}
                    leftIcon={
                      <Box
                        sx={{ 
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: '1.2rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <resource.icon />
                      </Box>
                    }
                    dense={dense}
                    sx={{
                      pl: 4,
                      '& .RaMenuItemLink-icon': {
                        minWidth: 36,
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.RaMenuItemLink-active': {
                        backgroundColor: `${group.color}15`,
                        borderRight: `3px solid ${group.color}`,
                        '& .RaMenuItemLink-icon': {
                          color: group.color,
                        },
                        '& .MuiTypography-root': {
                          color: group.color,
                          fontWeight: 500,
                        },
                      },
                    }}
                  />
                ))}
              </List>
            </Collapse>

            {/* Divider between groups */}
            {groupIndex < menuGroups.length - 1 && open && (
              <Divider sx={{ my: 1, mx: 2 }} />
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};
